import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import type { MachineTab } from './features/machines/components/TabBar.js';
import { LoginPage } from './features/auth/LoginPage.js';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage.js';
import { AuthGuard } from './features/auth/AuthGuard.js';
import { AppShell } from './components/layout/AppShell.js';
import { LiveDataProvider } from './features/live/LiveDataProvider.js';
import { ModeProvider } from './contexts/ModeContext.js';
import { MachineProvider } from './contexts/MachineContext.js';
import { DashboardPage } from './features/dashboard/DashboardPage.js';
import { MachineDetailPage } from './features/machines/MachineDetailPage.js';
import { ScaffoldPage } from './features/scaffold/ScaffoldPage.js';
import { ToolingPage } from './features/tooling/ToolingPage.js';
import { SettingsPage } from './features/settings/SettingsPage.js';
import { TeamManagementPage } from './features/team/TeamManagementPage.js';
import { AcceptInvitePage } from './features/auth/AcceptInvitePage.js';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

/**
 * Pathless layout route — wraps every protected page in AuthGuard + AppShell.
 * Children are matched at top-level paths (/dashboard, /machines/$id, …).
 */
const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: () => (
    <AuthGuard>
      <ModeProvider>
        <MachineProvider>
          <LiveDataProvider>
            <AppShell>
              <Outlet />
            </AppShell>
          </LiveDataProvider>
        </MachineProvider>
      </ModeProvider>
    </AuthGuard>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const VALID_TABS = new Set<MachineTab>(['overview', 'telemetry', 'alarms', 'tools']);

const machineDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/machines/$machineId',
  component: MachineDetailPage,
  validateSearch: (search: Record<string, unknown>): { tab: MachineTab } => ({
    tab: VALID_TABS.has(search.tab as MachineTab) ? (search.tab as MachineTab) : 'overview',
  }),
});

const toolingRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/tooling',
  component: ToolingPage,
});

const scheduleRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/schedule',
  component: () => (
    <ScaffoldPage title="Schedule" note="Job scheduling and capacity planning land in v1.1." />
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/settings',
  component: SettingsPage,
});

const teamRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/settings/team',
  component: TeamManagementPage,
});

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  component: AcceptInvitePage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  acceptInviteRoute,
  protectedLayoutRoute.addChildren([
    indexRoute,
    dashboardRoute,
    machineDetailRoute,
    toolingRoute,
    scheduleRoute,
    settingsRoute,
    teamRoute,
  ]),
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter(): JSX.Element {
  return <RouterProvider router={router} />;
}
